import { type ReactNode } from 'react';
import { useGetIdentity, useMenu, usePermissions } from '@refinedev/core';
import { Link, useLocation } from 'react-router';
import {
  BarChart3,
  Bot,
  CircleCheck,
  Clock,
  Database,
  FileText,
  Fingerprint,
  Globe,
  KeyRound,
  Layers,
  PenLine,
  Server,
  Plug,
  Puzzle,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { isAdminRoles } from '@/lib/require-admin';
import { useResourceCount } from '@/lib/use-resource-count';
import { cn } from '@/lib/utils';
import { useExtensionNavSections } from '@/extensions/use-extension-nav';
import { useHubVersionLabel } from '@/lib/hub-version';

// Exported as a test seam: `sidebar-sections.test` asserts every nav item
// buildVisibleSections emits resolves to an icon, so a resource added to
// resources.ts without a glyph here fails the suite instead of shipping a
// naked row (how `db-connectors` slipped through in M24).
export const ICONS: Record<string, LucideIcon> = {
  // M28 — Workspace section (every authenticated user).
  documents: FileText,
  integrations: Plug,
  account: UserRound,
  users: Users,
  'api-keys': KeyRound,
  // M27 — Relations renamed to Permissions (UI only).
  permissions: ShieldCheck,
  // M27 — identity providers + web research (ADM-07/15).
  'identity-providers': Fingerprint,
  'web-research': Globe,
  // M27 — model registry + per-purpose assignment (ADM-01/03).
  models: Bot,
  assignments: SlidersHorizontal,
  // M24 — DB connectors (DBC-01). `Plug` is already the integrations glyph, so
  // the datastore reads as its own thing.
  'db-connectors': Database,
  // HUB-107 — MCP servers (copilot tools).
  'mcp-servers': Server,
  tracks: Layers,
  prompts: PenLine,
  // APPR-07 / D-16 — approvals queue.
  approvals: CircleCheck,
  // S08 — owner scheduled actions.
  schedules: Clock,
  // HUB-115 — single audit events (who did what); Usage is the aggregate.
  audit: ScrollText,
  // M33 — adoption surface over the audit log (ATRU-06).
  usage: BarChart3,
  settings: Settings,
  // HUB-103 — every extension admin row (custom pages + declared settings).
  extension: Puzzle,
};

// Section grouping mirrors what each resource is about; order within a
// section follows resources.ts order. M27 (ADM-10/11) groups Users +
// Identity Providers + Web Research + Permissions under one "Identity and
// Permissions" heading and adds an "AI" section for the model surfaces.
// These are the ADMIN sections — shown only to admins (M28 ADM-11).
const SECTIONS: { label: string; names: string[] }[] = [
  { label: 'Identity and Permissions', names: ['users', 'identity-providers', 'web-research', 'permissions'] },
  { label: 'AI', names: ['models', 'assignments', 'mcp-servers'] },
  { label: 'Directory', names: ['api-keys', 'tracks'] },
  { label: 'Copilot', names: ['prompts', 'approvals'] },
  // M33 — Usage sits beside Settings: a deployment-wide read, admin-only.
  // HUB-115 — Audit log leads the section: the event list the Usage counts
  // summarise. Nav entry is admin-only; the route itself is auth-only.
  { label: 'System', names: ['audit', 'usage', 'settings'] },
];

// M28 (ADM-11; COP-117 D-13) — the Workspace section is HARDCODED (like the
// sidebar brand block), not driven by refine resources, so a non-admin with no
// resources still sees it. Both links land on the /me/* shell; Documents opens
// the SAME component admins reach at /admin/documents (one component, no fork).
const WORKSPACE_ITEMS: NavItem[] = [
  { key: 'ws-documents', name: 'documents', route: '/me/documents', label: 'Documents' },
  { key: 'ws-integrations', name: 'integrations', route: '/me/integrations', label: 'Integrations' },
  // S08 — the owner's own schedules and runs (every hub call is user-scoped).
  { key: 'ws-schedules', name: 'schedules', route: '/admin/schedules', label: 'Scheduled actions' },
  { key: 'ws-account', name: 'account', route: '/me/account', label: 'Account' },
];

// The minimal shape `buildVisibleSections` reads off refine's `useMenu` items.
export interface MenuItemLike {
  key: string;
  name: string;
  route?: string;
  label?: ReactNode;
}

export interface NavItem {
  key: string;
  name: string;
  route: string;
  label: ReactNode;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

/**
 * Pure role-gate for the sidebar (M28 ADM-11). Every authenticated user sees
 * the Workspace section; the admin sections (resolved from refine's menu
 * items) are appended ONLY for `isAdminRoles(roles)`. A non-admin therefore
 * sees ONLY Workspace — the same fail-closed decision `RequireAdmin` makes for
 * the /admin/* routes. Kept pure (no hooks) so it is unit-testable without a
 * refine/DOM context.
 */
export function buildVisibleSections(
  roles: unknown,
  menuItems: MenuItemLike[],
  extensionSections: NavSection[] = [],
): NavSection[] {
  const workspace: NavSection = { label: 'Workspace', items: WORKSPACE_ITEMS };
  if (!isAdminRoles(roles)) return [workspace];

  const byName = new Map(menuItems.map((item) => [item.name, item]));
  // Any resource not claimed by a section still renders, so a new entry in
  // resources.ts can't silently vanish from the nav.
  const claimed = new Set(SECTIONS.flatMap((s) => s.names));
  const unclaimed = menuItems.filter((item) => !claimed.has(item.name)).map((item) => item.name);
  const sectionDefs = unclaimed.length > 0 ? [...SECTIONS, { label: 'Other', names: unclaimed }] : SECTIONS;

  const adminSections = sectionDefs
    .map((section) => ({
      label: section.label,
      items: section.names
        .map((name) => byName.get(name))
        .filter((item): item is MenuItemLike => item !== undefined)
        .map((item) => ({
          key: item.key,
          name: item.name,
          route: item.route ?? '/',
          label: item.label ?? item.name,
        })),
    }))
    .filter((section) => section.items.length > 0);

  // HUB-103 — one section per enabled extension (headed by its name, so a row
  // like "Items" is never ambiguous), after the resource sections; absent when
  // no enabled extension has a page or a settings manifest.
  return [workspace, ...adminSections, ...extensionSections];
}

export interface SidebarBrand {
  subtitle: 'Admin' | 'Workspace';
  to: string;
  ariaLabel: string;
}

/**
 * The brand block names the area the user is in and links to its start. An
 * admin lands on the `/admin` dashboard; a non-admin cannot open it (RequireAdmin
 * sends them to `/`), so their block reads "Workspace" and links to the track
 * list (owner decision 2026-10-11).
 */
export function sidebarBrand(roles: unknown): SidebarBrand {
  return isAdminRoles(roles)
    ? { subtitle: 'Admin', to: '/admin', ariaLabel: 'Admin dashboard' }
    : { subtitle: 'Workspace', to: '/', ariaLabel: 'Home' };
}

export function Sidebar() {
  const { menuItems } = useMenu();
  const location = useLocation();
  const { data: identity } = useGetIdentity<{ email?: string; name?: string | null }>();
  const { data: roles } = usePermissions<unknown>({});

  // Fixed hook set — counts for the whole-collection resources shown as
  // right-aligned badges (others render without one).
  const counts: Record<string, number | undefined> = {
    users: useResourceCount('users'),
    'api-keys': useResourceCount('api-keys'),
    tracks: useResourceCount('tracks'),
  };

  const extensionSections = useExtensionNavSections(roles);
  const sections = buildVisibleSections(roles, menuItems as MenuItemLike[], extensionSections);

  const initial = (identity?.name || identity?.email || '?').trim().slice(0, 1).toUpperCase();
  const hubVersion = useHubVersionLabel();
  const brand = sidebarBrand(roles);

  return (
    <aside className="flex h-full w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
      {/* The brand block is the way back to the start (`sidebarBrand`): the
          `/admin` dashboard for an admin — it has no nav entry of its own, so
          without this a section is a dead end — and the track list otherwise. */}
      <Link
        to={brand.to}
        aria-label={brand.ariaLabel}
        aria-current={location.pathname === brand.to ? 'page' : undefined}
        className={cn(
          'mx-3 mt-3 flex items-center gap-2.5 rounded-[var(--radius-ctl)] px-1.5 py-1.5 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          location.pathname === brand.to ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent/40',
        )}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
          T
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-tight">Trackframe</span>
          <span className="block text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
            {brand.subtitle}
          </span>
        </span>
      </Link>
      <nav className="flex-1 overflow-y-auto p-3">
        {sections.map((section) => {
          if (section.items.length === 0) return null;
          return (
            <div key={section.label}>
              <div className="px-3 pb-1 pt-4 text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground first:pt-1">
                {section.label}
              </div>
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const active =
                    location.pathname === item.route ||
                    location.pathname.startsWith(item.route + '/');
                  const Icon = ICONS[item.name];
                  const count = counts[item.name];
                  return (
                    <li key={item.key}>
                      <Link
                        to={item.route}
                        className={cn(
                          'flex items-center gap-2.5 rounded-[var(--radius-ctl)] px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          active
                            ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
                            : 'hover:bg-sidebar-accent/40',
                        )}
                      >
                        {Icon && <Icon className="size-4 shrink-0 opacity-80" aria-hidden="true" />}
                        <span className="truncate">{item.label ?? item.name}</span>
                        {count !== undefined && (
                          <span
                            className={cn(
                              'ml-auto rounded-full px-1.5 text-[10px] font-semibold tabular-nums leading-4',
                              active
                                ? 'bg-card/60 text-sidebar-accent-foreground'
                                : 'border bg-muted text-muted-foreground',
                            )}
                          >
                            {count}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
      <div className="flex items-center gap-2.5 border-t border-sidebar-border p-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
          {initial}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold leading-tight">
            {identity?.name || identity?.email || '—'}
          </span>
          {identity?.name && identity?.email && (
            <span className="block truncate text-[10.5px] text-muted-foreground">{identity.email}</span>
          )}
        </span>
        <span className="text-[10px] tabular-nums text-muted-foreground">{hubVersion}</span>
      </div>
    </aside>
  );
}
